import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { FormControl, InputLabel, LinearProgress, MenuItem, Select } from "@mui/material";
import AppService from "../../../../../Configurations/Appservice";
import { Box } from "@mui/system";
import axios from "axios";
import { API_BASE_URL } from "../../../../../utils/constants";

export default function TopUpDialog(props) {
  const { onClose, paymentMethods, paymentTypes, onSuccess } = props;

  const [amount, setAmount] = React.useState();
  const [paymentMethod, setPaymentMethod] = React.useState("");
  const [paymentType, setPaymentType] = React.useState("");

  const [isLoading, loading] = React.useState(false);
  const [adminBalance, setAdminBalance] = React.useState(null); // ROOT_ADMIN org balance

  // Thaklis admin-specific state
  const [isThaklisAdmin, setIsThaklisAdmin] = React.useState(() => {
    try {
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      const principalInfo = authInfo.principalInfo || {};
      const roles = principalInfo.roles || [];
      const isRoot = Array.isArray(roles) && roles.some((r) => {
        const val = typeof r === 'string' ? r : (r?.name || r?.code || r?.authority || '');
        const s = String(val);
        return /(root\s*admin|role_root_admin|root_admin)/i.test(s) || (s.toLowerCase().includes('root') && s.toLowerCase().includes('admin'));
      });
      console.log('[TopUpDialog] Initial ROOT_ADMIN from localStorage:', isRoot, roles);
      return isRoot;
    } catch (e) {
      return false;
    }
  });
  const [partners, setPartners] = React.useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = React.useState("");

  // Local list of methods shown in the dropdown; seed from props and keep in sync
  const [methods, setMethods] = React.useState(Array.isArray(paymentMethods) ? paymentMethods : []);

  // Sync incoming props when they change
  React.useEffect(() => {
    if (Array.isArray(paymentMethods)) {
      setMethods(paymentMethods);
    }
  }, [paymentMethods]);

  // Detect whether current user is Thaklis admin and fetch partners if so
  React.useEffect(() => {
    const fetchUserAndPartners = async () => {
      try {
        // Only run when dialog opens
        if (!props.open) return;

        // Get current user info (via AppService so it adds auth headers / refreshes tokens)
        const meResp = await AppService.get("api/auth/me");
        const me = (meResp && meResp.data) ? meResp.data : (meResp || {});
        // Backend /api/auth/me returns roles; show dropdown ONLY for ROOT_ADMIN
        const roles = me?.roles || me?.principalInfo?.roles || [];
        const roleIsRoot = Array.isArray(roles) && roles.some((r) => {
          const val = typeof r === 'string' ? r : (r?.name || r?.code || r?.authority || '');
          const s = String(val);
          return /(root\s*admin|role_root_admin|root_admin)/i.test(s);
        });
        const isRootAdmin = roleIsRoot;
        console.log('[TopUpDialog] /api/auth/me check =>', { roles, isRootAdmin });
        setIsThaklisAdmin(isRootAdmin);

        if (isRootAdmin) {
          // Fetch partner organisations (key-value list)
          const partnerResp = await AppService.get("v1/organisation/keyValues?type=PARTNER");
          const items = Array.isArray(partnerResp) ? partnerResp : partnerResp?.data || [];
          setPartners(items);
          // Fetch ROOT_ADMIN org wallet balance for client-side validation
          try {
            const balResp = await AppService.get("v1/wallet/transactions/balance");
            const bal = balResp?.balance ?? balResp?.data?.balance;
            if (bal != null) setAdminBalance(Number(bal));
          } catch (_) {}
        } else {
          setPartners([]);
          setSelectedPartnerId("");
          setAdminBalance(null);
        }
      } catch (e) {
        console.error("Failed to determine admin or fetch partners", e);
        // Fallback: check localStorage for roles
        try {
          const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
          const principalInfo = authInfo.principalInfo || {};
          const roles = principalInfo.roles || [];
          const isRootAdmin = Array.isArray(roles) && roles.some((r) => {
            const val = typeof r === 'string' ? r : (r?.name || r?.code || r?.authority || '');
            const s = String(val);
            return /(root\s*admin|role_root_admin|root_admin)/i.test(s);
          });
          console.log('[TopUpDialog] Fallback from localStorage =>', { roles, isRootAdmin });
          setIsThaklisAdmin(isRootAdmin);
          if (isRootAdmin) {
            const partnerResp = await AppService.get("v1/organisation/keyValues?type=PARTNER");
            const items = Array.isArray(partnerResp) ? partnerResp : partnerResp?.data || [];
            setPartners(items);
            try {
              const balResp = await AppService.get("v1/wallet/transactions/balance");
              const bal = balResp?.balance ?? balResp?.data?.balance;
              if (bal != null) setAdminBalance(Number(bal));
            } catch (_) {}
          } else {
            setPartners([]);
            setSelectedPartnerId("");
            setAdminBalance(null);
          }
        } catch (fallbackErr) {
          console.error('Fallback role detection failed', fallbackErr);
          setIsThaklisAdmin(false);
          setPartners([]);
          setSelectedPartnerId("");
        }
      }
    };

    fetchUserAndPartners();
  }, [props.open]);

  // If already detected as ROOT_ADMIN from initial localStorage and dialog opens, fetch partners
  React.useEffect(() => {
    const fetchIfAdmin = async () => {
      if (!props.open || !isThaklisAdmin) return;
      try {
        const partnerResp = await AppService.get("v1/organisation/keyValues?type=PARTNER");
        const items = Array.isArray(partnerResp) ? partnerResp : partnerResp?.data || [];
        setPartners(items);
        // Also refresh admin balance when dialog opens
        try {
          const balResp = await AppService.get("v1/wallet/transactions/balance");
          const bal = balResp?.balance ?? balResp?.data?.balance;
          if (bal != null) setAdminBalance(Number(bal));
        } catch (_) {}
      } catch (e) {
        console.error('[TopUpDialog] Failed to fetch partners on admin init', e);
        setPartners([]);
        setAdminBalance(null);
      }
    };
    fetchIfAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, isThaklisAdmin]);

  // When dialog opens, if no methods provided, fetch user's payment methods
  React.useEffect(() => {
    const loadInitialMethods = async () => {
      try {
        if (!props.open) return;
        // If methods already present via props, skip
        if (methods && methods.length > 0) return;
        const resp = await AppService.get("v1/customerPaymentMethod/keyValues");
        const list = Array.isArray(resp) ? resp : resp?.data || [];
        setMethods(list);
      } catch (e) {
        console.error("Failed to load initial payment methods", e);
      }
    };
    loadInitialMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const handleSubmit = () => {
    // Basic validations
    const amountNum = Number(amount);
    if (!amountNum || isNaN(amountNum) || amountNum <= 0) {
      alert("Please enter a valid amount greater than 0");
      return;
    }
    if (!paymentType) {
      alert("Please select a payment type");
      return;
    }
    if (!paymentMethod) {
      alert("Please select a payment method");
      return;
    }

    // ROOT_ADMIN: if topping up (especially for partner), ensure amount <= ROOT_ADMIN wallet balance
    if (isThaklisAdmin && adminBalance != null) {
      if (amountNum > Number(adminBalance)) {
        alert(`Amount exceeds Root Admin wallet balance (${Number(adminBalance).toFixed(2)})`);
        return;
      }
    }

    loading(true);
    const payload = {
      amount: amountNum,
      paymentMethodId: paymentMethod,
    };

    // Attach selected partner if admin selected one
    if (isThaklisAdmin && selectedPartnerId) {
      payload.partnerOrganisationId = selectedPartnerId;
    }
    AppService.post("v1/topupRequest", payload)
      .then(async (response) => {
        try {
          // Fetch the latest wallet balance from server (current user's org)
          const balanceResp = await AppService.get("v1/wallet/transactions/balance");
          const latestBalance = balanceResp?.balance ?? balanceResp?.data?.balance;
          if (onSuccess && latestBalance !== undefined) {
            onSuccess(latestBalance);
          }
        } catch (e) {
          // Swallow balance refresh error; still close dialog
          console.error("Failed to refresh balance after top-up", e);
        } finally {
          onClose();
        }
      })
      .catch((error) => {
        const msg = error?.response?.data?.message || error?.message || "something went wrong";
        alert(msg);
      })
      .finally(() => {
        loading(false);
      });
  };

  const _onClose = () => {
    !isLoading && onClose();
  };

  const handleChange = (event) => {
    setPaymentMethod(event.target.value);
  };


  const handleChangePaymentType = (event) => {
    setPaymentType(event.target.value);
  };

  // Fetch filtered methods when payment type changes (always for logged-in user's organisation)
  React.useEffect(() => {
    const fetchMethodsForType = async () => {
      try {
        if (!paymentType) return;
        const resp = await AppService.get(`v1/wallet/paymentMethods/${paymentType}`);
        const list = resp?.paymentMethods || resp?.data?.paymentMethods || [];
        setMethods(list);
        // Reset chosen method if it no longer exists in the new list
        if (paymentMethod && !list.find((m) => m.id === paymentMethod)) {
          setPaymentMethod("");
        }
      } catch (e) {
        console.error("Failed to load methods for payment type", e);
      }
    };
    fetchMethodsForType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType]);


  return (
    <Dialog {...props} onClose={_onClose}>
      {isLoading && <LinearProgress />}
      <DialogTitle>{isThaklisAdmin ? 'Payment' : 'Top Up'}</DialogTitle>
      <DialogContent>
        <DialogContentText>{isThaklisAdmin ? 'Send request to add money to Partner wallet' :'Send request to add money to your wallet'}</DialogContentText>

        {isThaklisAdmin && (
          <Box my={3}>
            <FormControl fullWidth>
              <InputLabel id="partner-org-select-label">Partner Organization</InputLabel>
              <Select
                labelId="partner-org-select-label"
                value={selectedPartnerId}
                label="Partner Organization"
                onChange={(e) => setSelectedPartnerId(e.target.value)}
              >
                <MenuItem value="" disabled>Select Partner</MenuItem>
                {Array.isArray(partners) && partners.length > 0 ? (
                  partners.map((p) => (
                    <MenuItem key={(p.id || p.value)} value={(p.id || p.value)}>
                      {p.name || p.label || p.text || p.value || p.id}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="__none__" disabled>No partners available</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
        )}
        <Box my={3}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Payment Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              value={paymentType}
              label="Payment Type"
              onChange={handleChangePaymentType}
            >
              {paymentTypes.map((method) => (
                <MenuItem value={method.id}>{method.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box my={3}>
          <TextField
            autoFocus
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
            }}
            // variant="standard"
          />
        </Box>
        <Box my={3}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Payment Method</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              value={paymentMethod}
              label="Payment Method"
              onChange={handleChange}
              // variant="standard"
            >
              {methods.map((method) => (
                <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isLoading} onClick={handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
